import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Trash2, UserPlus, UserMinus, RefreshCw, ArrowRightLeft, Calendar, Clock, Building, Briefcase, User, MessageSquare, Key, Mail, Paperclip, Plus, Download, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useRBAC } from "../contexts/RBACContext";

const REQUEST_TYPES = [
  { value: "new", label: "New Hire", icon: UserPlus, color: "bg-green-500" },
  { value: "term", label: "Termination", icon: UserMinus, color: "bg-red-500" },
  { value: "change", label: "Change", icon: RefreshCw, color: "bg-blue-500" },
  { value: "transfer", label: "Transfer", icon: ArrowRightLeft, color: "bg-purple-500" },
  { value: "leave", label: "Leave", icon: Calendar, color: "bg-yellow-500" }
];

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "in_progress", label: "In Progress", color: "bg-purple-100 text-purple-800" },
  { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  { value: "closed", label: "Closed", color: "bg-gray-100 text-gray-800" }
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "bg-green-100 text-green-700" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-700" },
  { value: "critical", label: "Critical", color: "bg-red-100 text-red-700" }
];

export default function HRRequestDetail({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const { requestId } = useParams();
  const navigate = useNavigate();
  
  const effectiveRole = previewRole || user.role;
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Notes/Comments
  const [newNote, setNewNote] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [isPrivateNote, setIsPrivateNote] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");
  const { hasPermission } = useRBAC();
  const canViewPrivateNotes = hasPermission('ticket_private_comment', effectiveRole);
  const canCreatePrivateNotes = hasPermission('ticket_private_comment', effectiveRole);
  const canEditDeleteNotes = effectiveRole === 'super_admin' || effectiveRole === 'admin';
  
  // Update form - mirror IT Ticket fields
  const [updateData, setUpdateData] = useState({
    status: "",
    priority: "medium",
    assigned_to: null,
    additional_emails: []
  });
  
  // Department Admins for assignment
  const [assignableStaff, setAssignableStaff] = useState([]);

  // Send Credentials state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsData, setCredentialsData] = useState({
    recipient_email: "",
    credentials_text: "",
    expiration_type: "24_hours"
  });
  const [sendingCredentials, setSendingCredentials] = useState(false);

  const canManageRequests = user.role === "admin" || user.role === "super_admin";

  useEffect(() => {
    fetchRequestDetails();
    fetchAssignableStaff();
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      const response = await axios.get(`${API}/hr-requests/${requestId}`);
      setRequest(response.data);
      setUpdateData({
        status: response.data.status || "new",
        priority: response.data.priority || "medium",
        assigned_to: response.data.assigned_to || null,
        additional_emails: response.data.additional_emails || []
      });
    } catch (error) {
      toast.error("Failed to load HR request details");
      navigate("/tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignableStaff = async () => {
    try {
      // Use HR-specific staff endpoint for HR requests
      const response = await axios.get(`${API}/users/hr-staff`);
      setAssignableStaff(response.data);
    } catch (error) {
      console.error("Failed to fetch assignable staff:", error);
    }
  };

  const handleUpdateRequest = async () => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/hr-requests/${requestId}`, updateData);
      toast.success("HR Request updated");
      fetchRequestDetails();
    } catch (error) {
      toast.error("Failed to update request");
    } finally {
      setUpdating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/hr-requests/${requestId}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("File uploaded");
      fetchRequestDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    }
    e.target.value = "";
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/hr-requests/${requestId}/notes`, {
        content: newNote,
        is_private: isPrivateNote
      });
      setNewNote("");
      setIsPrivateNote(false);
      toast.success("Note added");
      fetchRequestDetails();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleEditNote = async (noteIndex) => {
    if (!editNoteContent.trim()) return;
    try {
      await axios.patch(`${API}/hr-requests/${requestId}/notes/${noteIndex}`, { content: editNoteContent });
      setEditingNoteIndex(null);
      setEditNoteContent("");
      toast.success("Note updated");
      fetchRequestDetails();
    } catch (error) {
      toast.error("Failed to update note");
    }
  };

  const handleDeleteNote = async (noteIndex) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await axios.delete(`${API}/hr-requests/${requestId}/notes/${noteIndex}`);
      toast.success("Note deleted");
      fetchRequestDetails();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const handleSendCredentials = async (e) => {
    e.preventDefault();
    
    if (!credentialsData.recipient_email || !credentialsData.credentials_text) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSendingCredentials(true);
    try {
      await axios.post(`${API}/secure-credentials`, {
        ticket_id: requestId,
        ticket_type: "hr_request",
        recipient_email: credentialsData.recipient_email,
        credentials_text: credentialsData.credentials_text,
        expiration_type: credentialsData.expiration_type
      });
      toast.success(`Credentials sent to ${credentialsData.recipient_email}`);
      setShowCredentialsModal(false);
      setCredentialsData({
        recipient_email: "",
        credentials_text: "",
        expiration_type: "24_hours"
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send credentials");
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!window.confirm(`Are you sure you want to delete this HR request? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setDeleting(true);
      await axios.delete(`${API}/hr-requests/${requestId}`);
      toast.success("HR request has been deleted");
      navigate("/tickets");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete request");
    } finally {
      setDeleting(false);
    }
  };

  const getRequestTypeInfo = (type) => {
    return REQUEST_TYPES.find(t => t.value === type) || REQUEST_TYPES[0];
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-slate-600 dark:text-white">Loading HR request...</div>
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const typeInfo = getRequestTypeInfo(request.request_type);
  const TypeIcon = typeInfo.icon;
  const statusInfo = getStatusInfo(request.status);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
      <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/tickets")}
            data-testid="back-to-tickets-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tickets
          </Button>
          
          {canManageRequests && (
            <Button
              variant="destructive"
              onClick={handleDeleteRequest}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-hr-request-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? "Deleting..." : "Delete Request"}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Request Header */}
            <Card className="border-0 shadow-md">
              <CardHeader className="border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full ${typeInfo.color} flex items-center justify-center`}>
                        <TypeIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-mono text-lg font-bold text-slate-700 dark:text-white" data-testid="request-number">
                          {request.request_number || 'N/A'}
                        </span>
                        <Badge className={`ml-3 ${typeInfo.color} text-white`}>
                          {typeInfo.label}
                        </Badge>
                        <Badge className={`ml-2 ${statusInfo.color}`}>
                          {statusInfo.label}
                        </Badge>
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2" data-testid="employee-name">
                      {request.employee_first_name} {request.employee_last_name}
                      {request.employee_suffix && ` ${request.employee_suffix}`}
                    </h1>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                      <span>Submitted by: <strong>{request.created_by_name}</strong></span>
                      <span>•</span>
                      <span>{new Date(request.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Job Title</p>
                        <p className="font-medium text-slate-800 dark:text-white">{request.job_title}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Dealership</p>
                        <p className="font-medium text-slate-800 dark:text-white">{request.dealership}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Department</p>
                        <p className="font-medium text-slate-800 dark:text-white">{request.department || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Effective Date</p>
                        <p className="font-medium text-slate-800 dark:text-white">
                          {(() => {
                            const dateParts = request.start_date ? request.start_date.split('-') : null;
                            return dateParts 
                              ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2]).toLocaleDateString()
                              : 'N/A';
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Employee ID</p>
                        <p className="font-medium text-slate-800 dark:text-white">
                          {request.employee_id_redacted ? "Redacted" : (request.employee_id || "Not provided")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Last Updated</p>
                        <p className="font-medium text-slate-800 dark:text-white">
                          {new Date(request.updated_at || request.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {request.notes && (
                  <>
                    <Separator className="my-6" />
                    <div>
                      <h3 className="font-semibold text-slate-700 dark:text-white mb-2">Additional Notes</h3>
                      <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                        {request.notes}
                      </p>
                    </div>
                  </>
                )}

                {request.additional_emails && request.additional_emails.length > 0 && (
                  <>
                    <Separator className="my-6" />
                    <div>
                      <h3 className="font-semibold text-slate-700 dark:text-white mb-2">Additional Emails</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {request.additional_emails.map((email) => (
                          <span key={email} className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-300 rounded-md flex items-center gap-1">
                            {email}
                            {canManageRequests && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const updated = request.additional_emails.filter(em => em !== email);
                                  try {
                                    await axios.patch(`${API}/hr-requests/${requestId}`, { additional_emails: updated });
                                    toast.success(`Removed ${email}`);
                                    fetchRequestDetails();
                                  } catch (err) {
                                    toast.error("Failed to remove email");
                                  }
                                }}
                                className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                                title={`Remove ${email}`}
                                data-testid={`hr-remove-email-${email}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Attachments Section */}
                <Separator className="my-6" />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-700 dark:text-white flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Attachments {request.attachments?.length ? `(${request.attachments.length})` : ''}
                    </h3>
                    <label className="cursor-pointer" data-testid="hr-add-attachment-btn">
                      <input type="file" className="hidden" onChange={handleFileUpload} />
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#7a1d42] bg-[#7a1d42]/10 hover:bg-[#7a1d42]/20 rounded-md transition-colors">
                        <Plus className="w-4 h-4" />
                        Add Attachment
                      </span>
                    </label>
                  </div>
                  {request.attachments && request.attachments.length > 0 ? (
                    <div className="space-y-2">
                      {request.attachments.map((attachment, index) => (
                        <div key={attachment.id || `att-${index}`} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Paperclip className="w-4 h-4 text-slate-400" />
                            <div>
                              <span className="text-sm text-slate-700 dark:text-white">{attachment.filename}</span>
                              {attachment.size && (
                                <span className="text-xs text-slate-500 ml-2">
                                  ({attachment.size < 1024 * 1024
                                    ? `${(attachment.size / 1024).toFixed(1)} KB`
                                    : `${(attachment.size / (1024 * 1024)).toFixed(1)} MB`})
                                </span>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`${API}/hr-requests/${request.id}/attachments/${attachment.id}`} target="_blank" rel="noopener noreferrer" data-testid={`hr-attachment-download-${index}`}>
                              <Download className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 dark:text-slate-500">No attachments yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Activity/Notes Section */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Activity & Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Add Note Form */}
                {canManageRequests && (
                  <form onSubmit={handleAddNote} className="mb-6">
                    {canCreatePrivateNotes && (
                      <div className="flex items-center gap-2 mb-3">
                        <Switch
                          id="hr-private-note"
                          checked={isPrivateNote}
                          onCheckedChange={setIsPrivateNote}
                        />
                        <Label htmlFor="hr-private-note" className="text-sm">Internal note (not visible to requester)</Label>
                      </div>
                    )}
                    <Textarea
                      placeholder={canCreatePrivateNotes ? "Add a note or internal note..." : "Add a note about this request..."}
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={3}
                      className="mb-3"
                      data-testid="hr-request-note-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={submittingNote || !newNote.trim()}
                      className="bg-[#7a1d42] hover:bg-[#5a1632]"
                      data-testid="add-hr-note-button"
                    >
                      {submittingNote ? "Adding..." : "Add Note"}
                    </Button>
                  </form>
                )}

                {/* Notes List */}
                <div className="space-y-4">
                  {request.activity_notes && request.activity_notes.filter(n => !n.is_private || canViewPrivateNotes).length > 0 ? (
                    request.activity_notes.map((note, actualIndex) => {
                      if (note.is_private && !canViewPrivateNotes) return null;
                      return (
                        <div key={note.id || `note-${actualIndex}`} className={`p-4 rounded-lg ${
                          note.is_private
                            ? "bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400"
                            : "bg-slate-50 dark:bg-slate-800"
                        }`} data-testid={`hr-note-${actualIndex}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700 dark:text-white">{note.author_name}</span>
                              {note.is_private && (
                                <Badge variant="outline" className="text-yellow-600 border-yellow-400">
                                  Internal
                                </Badge>
                              )}
                              {note.edited_at && (
                                <span className="text-xs text-slate-400 italic">(edited)</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-400">
                                {new Date(note.created_at).toLocaleString()}
                              </span>
                              {canEditDeleteNotes && (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => { setEditingNoteIndex(actualIndex); setEditNoteContent(note.content); }}
                                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400 hover:text-blue-600 transition-colors"
                                    title="Edit note"
                                    data-testid={`edit-hr-note-${actualIndex}`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteNote(actualIndex)}
                                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400 hover:text-red-600 transition-colors"
                                    title="Delete note"
                                    data-testid={`delete-hr-note-${actualIndex}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {editingNoteIndex === actualIndex ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editNoteContent}
                                onChange={(e) => setEditNoteContent(e.target.value)}
                                className="min-h-[60px] text-sm"
                                data-testid={`edit-hr-note-input-${actualIndex}`}
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleEditNote(actualIndex)} data-testid={`save-hr-note-${actualIndex}`}>Save</Button>
                                <Button size="sm" variant="ghost" onClick={() => { setEditingNoteIndex(null); setEditNoteContent(""); }}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{note.content}</p>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center text-slate-500 py-8">No activity notes yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Update HR Request - Mirror IT Ticket */}
            {canManageRequests && (
              <Card className="border-0 shadow-md">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle>Update HR Request</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={updateData.status}
                      onValueChange={(value) => setUpdateData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger data-testid="hr-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={updateData.priority}
                      onValueChange={(value) => setUpdateData(prev => ({ ...prev, priority: value }))}
                    >
                      <SelectTrigger data-testid="hr-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((priority) => (
                          <SelectItem key={priority.value} value={priority.value}>
                            {priority.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="assigned">Assign To</Label>
                    <Select
                      value={updateData.assigned_to || "unassigned"}
                      onValueChange={(value) => setUpdateData(prev => ({ ...prev, assigned_to: value === "unassigned" ? null : value }))}
                    >
                      <SelectTrigger data-testid="hr-assigned-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {assignableStaff.map((staff) => (
                          <SelectItem key={staff.id} value={staff.id}>
                            {staff.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Additional Emails</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUpdateData(prev => ({ ...prev, additional_emails: [...prev.additional_emails, ""] }))}
                        className="text-[#7a1d42] hover:text-[#5a1632] h-7 px-2"
                        data-testid="hr-add-email-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    {updateData.additional_emails.map((email, index) => (
                      <div key={`hr-email-${index}`} className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="email@example.com"
                          value={email}
                          onChange={(e) => {
                            setUpdateData(prev => {
                              const newEmails = [...prev.additional_emails];
                              newEmails[index] = e.target.value;
                              return { ...prev, additional_emails: newEmails };
                            });
                          }}
                          className="flex-1"
                          data-testid={`hr-email-input-${index}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setUpdateData(prev => ({ ...prev, additional_emails: prev.additional_emails.filter((_, i) => i !== index) }))}
                          className="text-red-500 hover:text-red-700 h-9 w-9"
                          data-testid={`hr-remove-email-btn-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full bg-[#7a1d42] hover:bg-[#5a1632]"
                    onClick={handleUpdateRequest}
                    disabled={updating}
                    data-testid="update-hr-request-button"
                  >
                    {updating ? "Updating..." : "Update HR Request"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Send Credentials */}
            {canManageRequests && (
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Secure Credentials
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => setShowCredentialsModal(true)}
                    className="w-full bg-[#7a1d42] hover:bg-[#5a1632]"
                    data-testid="send-credentials-button"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Send Credentials
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    Securely send login credentials to a recipient via encrypted link.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Request Summary */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg">Request Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Request Type</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`w-6 h-6 rounded-full ${typeInfo.color} flex items-center justify-center`}>
                      <TypeIcon className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-slate-800 dark:text-white">{typeInfo.label}</span>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Status</p>
                  <Badge className={STATUS_OPTIONS.find(s => s.value === request.status)?.color || "bg-gray-100 text-gray-800"}>
                    {STATUS_OPTIONS.find(s => s.value === request.status)?.label || request.status}
                  </Badge>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Priority</p>
                  <Badge className={
                    request.priority === "critical" ? "bg-red-100 text-red-800" :
                    request.priority === "high" ? "bg-orange-100 text-orange-800" :
                    request.priority === "medium" ? "bg-yellow-100 text-yellow-800" :
                    "bg-green-100 text-green-800"
                  }>
                    {request.priority ? request.priority.charAt(0).toUpperCase() + request.priority.slice(1) : "N/A"}
                  </Badge>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Employee</p>
                  <p className="font-medium text-slate-800 dark:text-white">
                    {request.employee_first_name} {request.employee_last_name}
                  </p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Dealership</p>
                  <p className="font-medium text-slate-800 dark:text-white">{request.dealership}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Department</p>
                  <p className="font-medium text-slate-800 dark:text-white">{request.department || "N/A"}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Job Title</p>
                  <p className="font-medium text-slate-800 dark:text-white">{request.job_title || "N/A"}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Assigned To</p>
                  <p className="font-medium text-slate-800 dark:text-white">{request.assigned_to_name || "Unassigned"}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Submitted</p>
                  <p className="font-medium text-slate-800 dark:text-white">
                    {new Date(request.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Send Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#7a1d42]" />
              Send Secure Credentials
            </DialogTitle>
            <DialogDescription>
              Securely send login credentials to a recipient. The credentials will be encrypted and accessible via a secure link.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSendCredentials} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recipient_email">Recipient Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="recipient_email"
                  type="email"
                  placeholder="recipient@example.com"
                  value={credentialsData.recipient_email}
                  onChange={(e) => setCredentialsData(prev => ({ ...prev, recipient_email: e.target.value }))}
                  className="pl-10"
                  data-testid="hr-credential-recipient-email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="credentials_text">Credentials / Notes *</Label>
              <Textarea
                id="credentials_text"
                placeholder="Paste your credentials here...&#10;&#10;Example:&#10;Username: john.doe&#10;Password: SecurePass123&#10;URL: https://example.com"
                value={credentialsData.credentials_text}
                onChange={(e) => setCredentialsData(prev => ({ ...prev, credentials_text: e.target.value }))}
                rows={8}
                className="font-mono text-sm"
                data-testid="hr-credential-text"
                required
              />
              <p className="text-xs text-slate-500">
                This content will be encrypted before sending.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiration_type">Link Expiration</Label>
              <Select
                value={credentialsData.expiration_type}
                onValueChange={(value) => setCredentialsData(prev => ({ ...prev, expiration_type: value }))}
              >
                <SelectTrigger data-testid="hr-credential-expiration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time view (expires after viewing)</SelectItem>
                  <SelectItem value="24_hours">24 hours</SelectItem>
                  <SelectItem value="7_days">7 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCredentialsModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendingCredentials}
                className="bg-[#7a1d42] hover:bg-[#5a1632]"
                data-testid="hr-send-credentials-submit"
              >
                {sendingCredentials ? "Sending..." : "Send Credentials"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
